/**
 * CreditNoteTypeBadge Component
 *
 * Displays a coloured badge indicating the type of credit note.
 * Uses badge variants from the design system for consistent styling.
 * Optionally shows Xero sync status indicator.
 *
 * User Story: US-REC-001 - Invoice Reconciliation
 */

import { Badge } from '@/components/ui/badge'
import { XeroSyncStatusIndicator } from './XeroSyncStatusIndicator'
import { cn } from '@/lib/utils'

/**
 * Mapping of credit note types to display labels
 */
const TYPE_LABELS = {
  write_off: 'Write Off',
  adjustment: 'Adjustment',
  refund: 'Refund',
  goodwill: 'Goodwill',
  legacy_settlement: 'Legacy Settlement'
}

/**
 * Mapping of credit note types to badge variants
 */
const TYPE_VARIANTS = {
  write_off: 'credit-note-write-off',
  adjustment: 'credit-note-adjustment',
  refund: 'credit-note-refund',
  goodwill: 'credit-note-goodwill',
  legacy_settlement: 'credit-note-legacy-settlement'
}

/**
 * CreditNoteTypeBadge displays the credit note type with appropriate styling
 * @param {Object} props - Component props
 * @param {string} props.type - Credit note type (write_off, adjustment, refund, goodwill, legacy_settlement)
 * @param {string} props.syncStatus - Xero sync status (synced, pending, failed, not_synced, not_applicable)
 * @param {string} props.syncError - Sync error message (if status is failed)
 * @param {string} props.xeroNumber - Xero credit note reference number
 * @param {function} props.onRetry - Callback for retry button (only shown when failed)
 * @param {boolean} props.retrying - Whether retry is in progress
 * @param {string} props.className - Additional CSS classes
 */
export function CreditNoteTypeBadge({
  type,
  syncStatus,
  syncError,
  xeroNumber,
  onRetry,
  retrying = false,
  className
}) {
  const label = TYPE_LABELS[type] || type
  const variant = TYPE_VARIANTS[type] || 'default'

  // Show sync status only for non-legacy and non-not_applicable
  const showSyncStatus = syncStatus && syncStatus !== 'not_applicable'

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <Badge variant={variant}>
        {label}
      </Badge>
      {showSyncStatus && (
        <XeroSyncStatusIndicator
          status={syncStatus}
          syncError={syncError}
          xeroNumber={xeroNumber}
          onRetry={onRetry}
          retrying={retrying}
          size="sm"
        />
      )}
    </span>
  )
}

export default CreditNoteTypeBadge
